/** Length formatting/parsing. Internal unit is decimal FEET everywhere in the doc. */

/**
 * 10.5208… → `10'-6 1/4"`. `accuracyIn` is the rounding increment in INCHES
 * : 1/64 (the default — MODEL PRECISION round, Matt 2026-10-08: "I don't want
 * dimensions to round at all. Let them be real up to 1/64"), 1/4, 1/2, 1… — the
 * value rounds to the nearest increment and the fraction displays reduced. Every
 * modeled length is itself stored on the 1/64" lattice (precision.ts), so at the
 * default a displayed length IS the stored length.
 *
 * ZERO-FEET SUPPRESSION (Matt, 2026-08-17): a length under a foot reads as
 * INCHES ONLY — `6"`, `5 1/2"`, `1/2"`, and exact zero as `0"` — never `0'-6"`
 * (the drafting convention). Anything a FOOT or more keeps the `ft'-in"` form.
 * Identity strings that must stay byte-stable (materialized auto type names —
 * the firm-library adopt-by-name contract) use `formatFtInExplicit` instead.
 */
export function formatFtIn(feet: number, accuracyIn = 1 / 64): string {
  return formatFtInCore(feet, accuracyIn, false)
}

/**
 * The pre-2026-08-17 always-feet form (`0'-6"`, `0'-0"`) — reserved for
 * MATERIALIZED identity strings (typeNaming.ts's auto type names), where a
 * display-format change would silently break adopt-by-name matching against
 * names already stored in models and the firm library. Display surfaces use
 * `formatFtIn`.
 */
export function formatFtInExplicit(feet: number, accuracyIn = 1 / 16): string {
  return formatFtInCore(feet, accuracyIn, true)
}

function formatFtInCore(feet: number, accuracyIn: number, explicitFeet: boolean): string {
  if (!isFinite(feet)) return '—'
  const acc = accuracyIn > 0 ? accuracyIn : 1 / 16
  const sign = feet < 0 ? '-' : ''
  const totalIn = Math.round(Math.abs(feet) * 12 / acc) * acc
  let ft = Math.floor(totalIn / 12 + 1e-9)
  let inch = totalIn - ft * 12
  if (Math.abs(inch - 12) < 1e-9) { ft += 1; inch = 0 }
  const whole = Math.floor(inch + 1e-9)
  let frac64 = Math.round((inch - whole) * 64)
  let w = whole
  if (frac64 === 64) { w += 1; frac64 = 0 }
  let fracStr = ''
  if (frac64 > 0) {
    let n = frac64, d = 64
    while (n % 2 === 0) { n /= 2; d /= 2 }
    fracStr = ` ${n}/${d}`
  }
  const inStr = w === 0 && fracStr ? fracStr.trim() : `${w}${fracStr}`
  if (!explicitFeet && ft === 0) return `${sign}${inStr}"`
  // MODEL PRECISION round: with feet shown, zero inches reads `23'-0 19/64"`, never the
  // ambiguous `23'-19/64"` (the drafting convention). The explicit form keeps the old
  // spelling — it builds adopt-by-name TYPE NAMES, which must stay byte-stable.
  if (!explicitFeet && w === 0 && fracStr) return `${sign}${ft}'-0${fracStr}"`
  return `${sign}${ft}'-${inStr}"`
}

/**
 * 2.6667 ft → `32"`, 2.5417 ft → `30 1/2"` — ALL-INCHES display (Matt, 2026-08-10:
 * door/window sizes read in inches, the vendor-catalog/schedule convention, never
 * foot-inches). Same rounding/fraction rules as formatFtIn, no feet part.
 */
export function formatInches(feet: number, accuracyIn = 1 / 64): string {
  if (!isFinite(feet)) return '—'
  const acc = accuracyIn > 0 ? accuracyIn : 1 / 16
  const sign = feet < 0 ? '-' : ''
  const totalIn = Math.round(Math.abs(feet) * 12 / acc) * acc
  const whole = Math.floor(totalIn + 1e-9)
  let frac64 = Math.round((totalIn - whole) * 64)
  let w = whole
  if (frac64 === 64) { w += 1; frac64 = 0 }
  let fracStr = ''
  if (frac64 > 0) {
    let n = frac64, d = 64
    while (n % 2 === 0) { n /= 2; d /= 2 }
    fracStr = ` ${n}/${d}`
  }
  return `${sign}${w === 0 && fracStr ? fracStr.trim() : `${w}${fracStr}`}"`
}

/**
 * Parse an INCHES-FIRST length to decimal feet — the input counterpart of
 * formatInches: a bare number/fraction reads as inches (`32` = 32", `30 1/2` =
 * 30.5"), while anything carrying an explicit foot mark (`2'-8"`) still parses
 * through parseLength unchanged. Returns null when unparseable.
 */
export function parseInches(input: string): number | null {
  const s = input.trim()
  if (!s) return null
  if (s.includes("'") || s.includes('‘') || s.includes('’')) return parseLength(s)
  return parseLength(s.endsWith('"') || s.endsWith('“') || s.endsWith('”') ? s : s + '"')
}

/**
 * Parse a length to decimal feet. Accepts: `10`, `10.5`, `10'`, `10'-6"`, `10' 6"`,
 * `10 6` (ft in), `6"`, `1/2"`, `10'-6 1/2"`. Returns null when unparseable.
 * DIMENSIONS SUITE: a leading `+` parses too (`+3'6"` → 3.5) — the relative-typed-
 * commit gestures read the SIGN off the raw text; the parser just mustn't choke.
 */
export function parseLength(input: string): number | null {
  const s = input.trim().replace(/[""]/g, '"').replace(/['']/g, "'")
  if (!s) return null
  const neg = s.startsWith('-')
  const body = neg || s.startsWith('+') ? s.slice(1) : s

  const frac = (t: string): number | null => {
    const m = t.match(/^(\d+)\s*\/\s*(\d+)$/)
    if (m) { const d = Number(m[2]); return d ? Number(m[1]) / d : null }
    const n = Number(t)
    return isFinite(n) ? n : null
  }
  const inches = (t: string): number | null => {
    // `6`, `6.5`, `1/2`, `6 1/2`
    const m = t.match(/^(\d+(?:\.\d+)?)\s+(\d+\s*\/\s*\d+)$/)
    if (m) { const a = Number(m[1]); const b = frac(m[2]); return b == null ? null : a + b }
    return frac(t)
  }

  let ftPart: string | null = null
  let inPart: string | null = null
  const fm = body.match(/^([\d.]+)\s*'\s*(?:-?\s*(.*?)\s*"?\s*)?$/)
  if (fm) { ftPart = fm[1]; inPart = fm[2] || null }
  else if (body.endsWith('"')) { inPart = body.slice(0, -1).trim() }
  else {
    const two = body.match(/^([\d.]+)\s+([\d.\s/]+)$/) // `10 6` = ft in
    if (two) { ftPart = two[1]; inPart = two[2].trim() }
    else ftPart = body
  }

  let feet = 0
  if (ftPart != null) { const f = Number(ftPart); if (!isFinite(f)) return null; feet += f }
  if (inPart != null && inPart !== '') { const i = inches(inPart.replace(/"$/, '').trim()); if (i == null) return null; feet += i / 12 }
  return neg ? -feet : feet
}
